import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../../../config/api';

const DashboardStats = () => {
  const [stats, setStats] = useState({
    totalMatches: 0,
    publishedMatches: 0,
    liveMatches: 0,
    scheduledMatches: 0,
    endedMatches: 0,
    totalAdmins: 0,
    totalVotes: 0,
    totalFlags: 0
  });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/admin/stats`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
    setLoading(false);
  };

  // Quick Actions Navigation Handlers
  const handleAddMatch = () => {
    navigate('/admin/dashboard', { state: { activeTab: 'matches', openForm: true } });
  };

  const handleViewMatches = () => {
    navigate('/admin/dashboard', { state: { activeTab: 'matches' } });
  };

  const handleUploadFlag = () => {
    navigate('/admin/dashboard', { state: { activeTab: 'flags', openForm: true } });
  };

  const handleManageAdmins = () => {
    navigate('/admin/dashboard', { state: { activeTab: 'admins', openForm: true } });
  };

  const StatCard = ({ title, value, icon, color }) => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 transition-transform duration-200 hover:shadow-md">
      <div className="flex items-center justify-between">
        <div className="flex-1 min-w-0">
          <p className="text-xs sm:text-sm font-medium text-gray-600 truncate">{title}</p>
          <div className="mt-2">
            {loading ? (
              <div className="animate-pulse">
                <div className="h-6 sm:h-8 bg-gray-200 rounded w-16"></div>
              </div>
            ) : (
              <p className={`text-2xl sm:text-3xl font-bold ${color}`}>
                {typeof value === 'number' ? value.toLocaleString() : value}
              </p>
            )}
          </div>
        </div>
        <div className={`w-12 h-12 sm:w-16 sm:h-16 rounded-full ${color.replace('text-', 'bg-').replace('-600', '-100')} flex items-center justify-center flex-shrink-0 ml-4`}>
          <span className="text-xl sm:text-2xl">{icon}</span>
        </div>
      </div>
    </div>
  );

  const statsData = [
    { title: 'Total Matches', value: stats.totalMatches, icon: '⚽', color: 'text-blue-600' },
    { title: 'Published Matches', value: stats.publishedMatches, icon: '📢', color: 'text-green-600' },
    { title: 'Live Matches', value: stats.liveMatches, icon: '🔴', color: 'text-red-600' },
    { title: 'Scheduled Matches', value: stats.scheduledMatches, icon: '📅', color: 'text-yellow-600' },
    { title: 'Ended Matches', value: stats.endedMatches, icon: '🏁', color: 'text-gray-600' },
    { title: 'Total Flags', value: stats.totalFlags, icon: '🏳️', color: 'text-indigo-600' },
    { title: 'Total Admins', value: stats.totalAdmins, icon: '👥', color: 'text-purple-600' },
    { title: 'Total Votes', value: stats.totalVotes, icon: '🗳️', color: 'text-pink-600' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2 sm:mb-0">Dashboard Overview</h2>
        <p className="text-sm text-gray-600">Real-time statistics</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
        {statsData.map((stat, index) => (
          <StatCard
            key={index}
            title={stat.title}
            value={stat.value}
            icon={stat.icon}
            color={stat.color}
          />
        ))}
      </div>

      {/* Additional dashboard content */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h3>
          <div className="space-y-3">
            <div className="flex items-center text-sm">
              <div className="w-2 h-2 bg-green-400 rounded-full mr-3"></div>
              <span className="text-gray-600">New match created</span>
            </div>
            <div className="flex items-center text-sm">
              <div className="w-2 h-2 bg-blue-400 rounded-full mr-3"></div>
              <span className="text-gray-600">Flag uploaded</span>
            </div>
            <div className="flex items-center text-sm">
              <div className="w-2 h-2 bg-yellow-400 rounded-full mr-3"></div>
              <span className="text-gray-600">Match status updated</span>
            </div>
            <div className="flex items-center text-sm">
              <div className="w-2 h-2 bg-purple-400 rounded-full mr-3"></div>
              <span className="text-gray-600">Admin user added</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <button
              onClick={handleAddMatch}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors duration-200 text-sm font-medium flex items-center justify-center space-x-2"
            >
              <span>⚽</span>
              <span>Add New Match</span>
            </button>
            <button
              onClick={handleViewMatches}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors duration-200 text-sm font-medium flex items-center justify-center space-x-2"
            >
              <span>📋</span>
              <span>View All Matches</span>
            </button>
            <button
              onClick={handleUploadFlag}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors duration-200 text-sm font-medium flex items-center justify-center space-x-2"
            >
              <span>🏳️</span>
              <span>Upload Flags</span>
            </button>
            <button
              onClick={handleManageAdmins}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors duration-200 text-sm font-medium flex items-center justify-center space-x-2"
            >
              <span>👥</span>
              <span>Manage Admins</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardStats;
